"use client";

import React, { useCallback, useEffect, useRef } from "react";
import { ProductCardSSRContent } from "@/components/ProductCard/innerContent";
import {
  getBrandStringFromPartnerKeys,
  getTagularSubject,
  setCorrelationId,
} from "@/utils/tagular/helpers";
import { cn } from "@/lib/utils";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  FormatSubType,
  FormatType,
  NYI_NUMBER,
  NYI_STRING,
} from "@/constants/tagular/main";
import { PRODUCT_INDEX_EN } from "@/constants/algolia";
import { translateVariant } from "@/utils/tagular/dataTranslationMatrices";
import { ProductClicked } from "@/hooks/eventing/types/redventures.ecommerce.v1.ProductClicked";
import useProductEvents from "@/hooks/eventing/useProductEvents";
import { useInView } from "react-intersection-observer";
import { ProductCardProps } from "@/components/ProductCard/types";
import { isElementHidden } from "@/utils/isElementHidden";
import { bindProductCardClickedEvent as bindSegmentProductCardClickedEvent } from "@/lib/segment";
import useWebview from "@/webview/useWebview";
import ProductCardWrapper from "./ProductCardWrapper";
import { createCustomDimensions } from "@/utils/tagular/createDimension";

export function ClientSideProductCard({ onClick, ...props }: ProductCardProps) {
  const {
    partnerKey,
    name,
    href,
    location,
    tagular_variant,
    subjects,
    index = -1,
    formatType = FormatType.ProductCard,
    formatSubtype = FormatSubType.Grid,
    skipViewEvent = false,
    queryId,
    uuid,
    listId,
    searchQueryId,
    resultsIndex,
  } = props;
  const { getUrl } = useWebview();
  const tagularProduct: ProductClicked["product"] = {
    brand: getBrandStringFromPartnerKeys(
      Array.isArray(partnerKey) ? partnerKey : [partnerKey ?? ""],
    ),
    formatSubtype,
    formatType,
    location: location || NYI_STRING,
    name: name,
    variant: tagular_variant || translateVariant(props.type),
    position: index < 0 ? NYI_NUMBER : index + 1,
    category: getTagularSubject(subjects),
    productId: uuid,
  };

  const customDimensions: ProductClicked["customDimensions"] =
    createCustomDimensions([
      ["level", props.level],
      ["courseCount", props.childCoursesCount],
      ["pacing", props.pacing],
      ["isPartOfProgram", props.isPartOfProgram],
      ["learningType", props.type],
      ["weeksToComplete", props.weeksToComplete],
      ["searchQueryId", searchQueryId],
    ]);

  const { productClicked, productViewed, correlationId } = useProductEvents({
    product: tagularProduct,
    customDimensions,
  });

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    setCorrelationId(correlationId);

    productClicked({
      listId,
    });

    onClick?.(e);
  };

  const ref = useRef<HTMLAnchorElement>(undefined);

  const {
    ref: inViewRef,
    inView,
    entry,
  } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  const setRefs = useCallback(
    (node: HTMLAnchorElement) => {
      ref.current = node;
      inViewRef(node);
    },
    [inViewRef],
  );

  useEffect(() => {
    if (!inView) {
      return;
    }

    bindSegmentProductCardClickedEvent({
      domElement: ref.current as Element,
      link: href,
      label: props.name,
      objectId: props.objectId,
      queryId: props?.queryId,
      index: props.resultsIndex,
      productSource: props.productSource,
      product: props.product,
      productCategory: props.type ?? "",
      resultLevel: props.resultLevel,
      uuid: props.uuid,
      title: props.parentTitle,
      activeRunKey: props.activeRunKey,
      position: (props.index ?? 0) + 1,
    });

    if (
      !skipViewEvent &&
      entry &&
      !isElementHidden(entry.target as HTMLElement)
    ) {
      productViewed({
        listId,
      });
    }
  }, [inView, listId, searchQueryId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set URL query params only if there is a query for Segment eventing
  let hrefWithQueryParams;

  if (!!queryId) {
    const url = new URL(href || "/", window.location.origin);

    const position = (index < 0 ? NYI_NUMBER : index + 1).toString();
    url.searchParams.set("index", resultsIndex || PRODUCT_INDEX_EN);
    url.searchParams.set("queryId", queryId);
    url.searchParams.set("position", position);

    hrefWithQueryParams = url.toString().replace(window.location.origin, "");
  }

  return (
    <a
      ref={setRefs}
      onClick={handleClick}
      href={queryId ? getUrl(hrefWithQueryParams) : getUrl(href)}
      className={cn(
        "no-underline hover:no-underline h-[332px] w-full max-w-[330px] mx-auto md:mx-0",
        props.className,
      )}
    >
      <ProductCardSSRContent {...props} />
    </a>
  );
}

export default function ProductCard(props: ProductCardProps) {
  return <ProductCardWrapper {...props} />;
}
